// src/upload-video.js
// Frontend uploader kept compatible with existing call sites:
// uploadFile(file, onProgress, path = '/')
// Returns a Promise that resolves to a string (public URL when possible).

const PRESIGNED_ENDPOINT = 'https://api.azad.deloai.com/generate-presigned-url';

export const uploadFile = (file, onProgress, path = '/') => {
  if (!file) return Promise.reject(new Error('No file provided'));

  // Normalize path: remove leading slash(es)
  const normalizedPath = (path || '').replace(/^\/+/, '');

  const body = {
    fileName: file.name,
    fileType: file.type || 'application/octet-stream',
    path: normalizedPath
  };

  return new Promise(async (resolve, reject) => {
    let presignJson;
    try {
      const presignResp = await fetch(PRESIGNED_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!presignResp.ok) {
        const text = await presignResp.text().catch(() => '');
        return reject(new Error(`Failed to get presigned URL: ${presignResp.status} ${text}`));
      }

      presignJson = await presignResp.json();
    } catch (err) {
      return reject(err);
    }

    const { uploadUrl, key } = presignJson || {};

    if (!uploadUrl) return reject(new Error('Backend did not return uploadUrl'));

    // Use XHR to get upload progress in browsers
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    // Ensure Content-Type matches what the presign expects
    xhr.setRequestHeader('Content-Type', body.fileType);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === 'function') {
        const percent = Math.round((event.loaded * 100) / event.total);
        try { onProgress(percent); } catch (e) { /* swallow progress callback errors */ }
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Try to return a usable public URL string (keeps compatibility with previous resolve(data.Location))
        // If your bucket objects are private, this will not be a publicly accessible URL —
        // consider having the backend return a presigned GET URL for downloads instead.
        let publicUrl;
        try {
          publicUrl = uploadUrl.split('?')[0];
        } catch {
          publicUrl = undefined;
        }

        // If we have a guessed publicUrl, resolve that string; otherwise resolve with the S3 key
        if (publicUrl) return resolve(publicUrl);
        if (key) return resolve(key);
        return resolve(''); // fallback to empty string to maintain return type
      } else {
        return reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during file upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));

    try {
      xhr.send(file);
    } catch (err) {
      reject(err);
    }
  });
};
